import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  useExportConfigKVs,
  useImportConfigKVs,
  useBulkDeleteConfigKVs
} from '@/app/hooks/useConfigKV'
import { Download, Upload, Trash2, AlertCircle, FileJson, FileText, Loader2 } from 'lucide-react'

interface ConfigKVBulkOperationsProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  scope: 'organization' | 'site'
  organizationId?: number
  siteId?: number
  selectedKeys?: string[]
}

const ConfigKVBulkOperations: React.FC<ConfigKVBulkOperationsProps> = ({
  open,
  onOpenChange,
  scope,
  organizationId,
  siteId,
  selectedKeys = []
}) => {
  const [activeTab, setActiveTab] = useState('export')
  const [exportFormat, setExportFormat] = useState<'json' | 'csv'>('json')
  const [importFile, setImportFile] = useState<File | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const exportMutation = useExportConfigKVs()
  const importMutation = useImportConfigKVs()
  const bulkDeleteMutation = useBulkDeleteConfigKVs()

  const handleExport = () => {
    exportMutation.mutate({
      scope,
      organization_id: organizationId,
      site_id: siteId,
      format: exportFormat
    })
  }

  const handleImport = () => {
    if (!importFile) return

    importMutation.mutate(
      {
        file: importFile,
        params: {
          scope,
          organization_id: organizationId,
          site_id: siteId
        }
      },
      {
        onSuccess: () => {
          setImportFile(null)
          onOpenChange(false)
        }
      }
    )
  }

  const handleBulkDelete = () => {
    if (selectedKeys.length === 0) return

    bulkDeleteMutation.mutate(
      {
        keys: selectedKeys,
        scope,
        organization_id: organizationId,
        site_id: siteId
      },
      {
        onSuccess: () => {
          setShowDeleteConfirm(false)
          onOpenChange(false)
        }
      }
    )
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const validTypes = ['application/json', 'text/csv']
      if (!validTypes.includes(file.type)) {
        alert('Please select a JSON or CSV file')
        return
      }
      setImportFile(file)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Bulk Operations</DialogTitle>
          <DialogDescription>
            Export, import, or delete multiple configuration values at once.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="export">
              <Download className="mr-2 h-4 w-4" /> Export
            </TabsTrigger>
            <TabsTrigger value="import">
              <Upload className="mr-2 h-4 w-4" /> Import
            </TabsTrigger>
            <TabsTrigger value="delete" disabled={selectedKeys.length === 0}>
              <Trash2 className="mr-2 h-4 w-4" /> Delete ({selectedKeys.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="export" className="space-y-4">
            <div className="space-y-2">
              <Label>Export Format</Label>
              <Select value={exportFormat} onValueChange={(v: any) => setExportFormat(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="json">
                    <div className="flex items-center">
                      <FileJson className="mr-2 h-4 w-4" /> JSON
                    </div>
                  </SelectItem>
                  <SelectItem value="csv">
                    <div className="flex items-center">
                      <FileText className="mr-2 h-4 w-4" /> CSV
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This will export all configuration values for the current {scope}.
                Encrypted values will be exported in their encrypted form.
              </AlertDescription>
            </Alert>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleExport} disabled={exportMutation.isPending}>
                {exportMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Export
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="import" className="space-y-4">
            <div className="space-y-2">
              <Label>Select File</Label>
              <Input
                type="file"
                accept=".json,.csv"
                onChange={handleFileChange}
              />
              {importFile && (
                <p className="text-sm text-muted-foreground">
                  Selected: {importFile.name}
                </p>
              )}
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>JSON files should contain an array of configuration objects</li>
                  <li>CSV files should have headers: key, value, description, data_type</li>
                  <li>Existing keys will be updated with the imported values</li>
                  <li>The scope will be set to the current {scope}</li>
                </ul>
              </AlertDescription>
            </Alert>

            {importMutation.data && importMutation.data.errors.length > 0 && (
              <Alert variant="destructive">
                <AlertDescription>
                  <p className="font-semibold">Import completed with errors:</p>
                  <ul className="list-disc list-inside mt-2">
                    {importMutation.data.errors.map((error, index) => (
                      <li key={index} className="text-sm">{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={!importFile || importMutation.isPending}
              >
                {importMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Import
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="delete" className="space-y-4">
            {!showDeleteConfirm ? (
              <>
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    You are about to delete {selectedKeys.length} configuration value(s).
                    This action cannot be undone.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label>Selected Keys:</Label>
                  <div className="max-h-[200px] overflow-auto rounded border p-2">
                    <ul className="space-y-1">
                      {selectedKeys.map((key) => (
                        <li key={key} className="font-mono text-sm">
                          {key}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Continue
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <p className="font-semibold">Final Confirmation</p>
                    <p className="mt-2">
                      Are you absolutely sure you want to delete these {selectedKeys.length} configuration values?
                      This action is permanent and cannot be reversed.
                    </p>
                  </AlertDescription>
                </Alert>

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Go Back
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleteMutation.isPending}
                  >
                    {bulkDeleteMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Delete {selectedKeys.length} Items
                  </Button>
                </DialogFooter>
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

export default ConfigKVBulkOperations